import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Typography,
  Box,
  Avatar,
  Button,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Person as PersonIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import api from '../config/axios';
import SocialPageShell, {
  socialTitleSx,
  socialSubtitleSx,
  socialGhostBtn,
  socialCardSx,
} from '../components/SocialPageShell';

const Following = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchFollowing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchFollowing = async () => {
    setLoading(true);
    try {
      const [followingResponse, userResponse] = await Promise.all([
        api.get(`/api/users/${userId}/following`),
        api.get(`/api/users/${userId}`),
      ]);
      setFollowing(followingResponse.data.following || []);
      setUser(userResponse.data.user);
    } catch (error) {
      console.error('Error fetching following:', error);
      setFollowing([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SocialPageShell maxWidth="sm">
      <Box sx={{ position: 'relative', textAlign: 'center', mb: 3, px: 4 }}>
        <IconButton
          onClick={() => navigate(-1)}
          size="small"
          sx={{
            position: 'absolute',
            left: 0,
            top: 0.25,
            color: 'var(--rl-muted)',
            '&:hover': { color: 'var(--rl-cream)', backgroundColor: 'rgba(244,239,230,0.06)' },
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography sx={socialTitleSx}>
          {user ? `${user.username}` : 'Following'}
        </Typography>
        <Typography sx={socialSubtitleSx}>
          Following {following.length} {following.length === 1 ? 'person' : 'people'}
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={28} sx={{ color: 'var(--rl-accent)' }} />
        </Box>
      ) : following.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <PersonIcon sx={{ fontSize: 48, color: 'rgba(244,239,230,0.2)', mb: 1.5 }} />
          <Typography sx={{ color: 'var(--rl-cream)', fontWeight: 600, mb: 0.5 }}>
            Not following anyone
          </Typography>
          <Typography sx={{ color: 'var(--rl-muted)', fontSize: '0.88rem' }}>
            This user isn’t following anyone yet.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {following.map((followedUser) => (
            <Box
              key={followedUser._id}
              onClick={() => navigate(`/profile/${followedUser._id}`)}
              sx={{
                ...socialCardSx,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'rgba(212,160,23,0.4)',
                  backgroundColor: 'rgba(212,160,23,0.05)',
                },
              }}
            >
              <Avatar
                src={followedUser.profilePicture}
                sx={{ width: 42, height: 42, bgcolor: 'rgba(244,239,230,0.08)' }}
              >
                {followedUser.username?.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <Typography
                  sx={{
                    color: 'var(--rl-cream)',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {followedUser.username}
                </Typography>
                {followedUser.bio && (
                  <Typography
                    sx={{
                      color: 'var(--rl-muted)',
                      fontSize: '0.75rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {followedUser.bio}
                  </Typography>
                )}
              </Box>
              <Button
                component={Link}
                to={`/profile/${followedUser._id}`}
                variant="outlined"
                size="small"
                onClick={(e) => e.stopPropagation()}
                sx={{ ...socialGhostBtn, py: 0.4, px: 1.1, fontSize: '0.72rem' }}
              >
                View
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </SocialPageShell>
  );
};

export default Following;
